import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import { AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { ExpandMoreRounded, ErrorOutline } from '@mui/icons-material';
import moment from 'moment';
import _ from 'lodash';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { FHAccordion, FHSelect, FProgressBar } from '../../../../../../base';
import { FORTA_BLUE, FORTA_GRAY2, FORTA_GRAY3 } from '../../../../../../../utils/colors';
import { sessionActions, goalActions } from '../../../../../../../store';
import { linearRegression } from '../../../../../../../helpers';
import { chartColors } from '../../../../../data-dashboard/constants';
import {
  DurationEventMeasurementUnitEnum,
  RateMethodMeasurementUnitsIntervalUnitEnum,
  TPGoalDataCollectionMethod,
} from '../../constants/general';
import { getChartLabelByDCM, getDataKeyByDCM } from '../../../../../../../pages/utils';
import { BackgroundTypes } from '../../../../../../../constants';
import {
  DurationChartPlotMethod,
  DurationChartPlotOptions,
} from '../../../../../../../pages/constants';

import type { RootState } from '../../../../../../../store';
import type {
  ITrialSummaryTarget,
  ITargetPhaseChange,
  ITreatmentPlanGoalTarget,
} from '../../../../../../../utils/types';

interface TargetProgressProps {
  goalId: number;
  trialSummary: ITrialSummaryTarget | null;
  target: ITreatmentPlanGoalTarget;
  onUpdateField: (key: string, value: any) => void;
}

const CustomTooltip = ({ active, payload, label, durationChartPlotMethod }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    const isPhaseChange = data.name === 'Phase change';

    return (
      <Box
        sx={{
          background: 'white',
          borderRadius: '4px',
          padding: 2,
          borderWidth: 1,
          borderColor: isPhaseChange ? FORTA_BLUE.primary : chartColors[0],
          borderStyle: 'solid',
        }}
      >
        <Typography>{`Date: ${moment(label).format('MM/DD/YY')}`}</Typography>
        {isPhaseChange ? (
          <>
            <Typography color={FORTA_BLUE.primary}>Phase change</Typography>
            <Box
              textAlign={'left'}
              sx={{
                mt: 1,
                p: 1,
                borderRadius: '4px',
                border: `1px solid ${FORTA_BLUE.primary}`,
              }}
            >
              <Typography color={FORTA_BLUE.primary}>{`Title: ${data.dataKey}`}</Typography>
            </Box>
          </>
        ) : (
          <Typography color={chartColors[0]}>
            {`${getChartLabelByDCM(data.name, durationChartPlotMethod)}: ${data.value}${
              data.name === TPGoalDataCollectionMethod.PERCENTAGE ? '%' : ''
            }`}
          </Typography>
        )}
      </Box>
    );
  }
  return null;
};

// Custom hook for chart calculations
function useChartCalculations(
  trialSummary: ITrialSummaryTarget | null,
  durationChartPlotMethod: DurationChartPlotMethod,
) {
  let dcm = '';
  let rateUnit = '';
  let durationUnit = '';
  if (trialSummary?.trials?.length) {
    dcm = trialSummary?.trials?.[0].data_collection_method ?? '';
    rateUnit = trialSummary?.trials?.[0].measurement_units_interval_unit ?? '';
    durationUnit = trialSummary?.trials?.[0].measurement_units_duration_unit ?? '';
  }
  const dataKey = getDataKeyByDCM(dcm, durationChartPlotMethod);

  const trendLineData = useMemo(() => {
    if (!trialSummary?.trials.length) return [];

    // Group trials by date
    const trialDataByDate = _.groupBy(trialSummary.trials, (trial) =>
      moment(trial.date)
        .utcOffset((trial.utc_offset ?? 0) / 60)
        .format('YYYY-MM-DD'),
    );

    // Aggregate data for each date
    const aggregatedTrials = Object.entries(trialDataByDate).map(([date, trialsOfDate]) => {
      const sumAttempts = trialsOfDate.reduce((sum, trial) => sum + trial.attempts, 0);
      const sumSuccess = trialsOfDate.reduce((sum, trial) => sum + trial.success, 0);
      const sumInstances = trialsOfDate.reduce((sum, trial) => sum + trial.instances, 0);

      // Calculate data for rate
      let rateTotal = 0;
      let sessionDurationTotal = 0;
      let sessionDurationTotalUnit = 1;
      let rateTimeUnit = '';

      trialsOfDate.forEach((trial) => {
        rateTotal += trial?.total_trial_rate_events ?? 0;
        sessionDurationTotal += trial?.expected_duration ?? 0;
        rateTimeUnit = trial?.measurement_units_interval_unit ?? '';
      });
      if (rateTimeUnit) {
        switch (rateTimeUnit) {
          case RateMethodMeasurementUnitsIntervalUnitEnum.HOUR:
            sessionDurationTotalUnit = sessionDurationTotal / 3600;
            break;
          case RateMethodMeasurementUnitsIntervalUnitEnum.MINUTE:
            sessionDurationTotalUnit = sessionDurationTotal / 60;
            break;
          case RateMethodMeasurementUnitsIntervalUnitEnum.SECOND:
            sessionDurationTotalUnit = sessionDurationTotal;
        }
      }
      const rate = parseFloat((rateTotal / sessionDurationTotalUnit).toFixed(2));

      // Calculate data for durations
      let durationEventTotal = 0;
      let durationEventAverage = 0;
      let durationEventCount = 0;
      let durationUnitConvertNum = 1;
      let durationTimeUnit = '';

      trialsOfDate.forEach((trial) => {
        durationEventTotal += trial?.duration_events?.reduce((sum, dur) => sum + dur, 0) ?? 0;
        durationEventCount += trial?.duration_events?.length ?? 0;
        durationTimeUnit = trial?.measurement_units_duration_unit ?? '';
      });

      if (durationTimeUnit) {
        switch (durationTimeUnit) {
          case DurationEventMeasurementUnitEnum.hour:
            durationUnitConvertNum = 3600;
            break;
          case DurationEventMeasurementUnitEnum.minute:
            durationUnitConvertNum = 60;
            break;
          case DurationEventMeasurementUnitEnum.second:
            durationUnitConvertNum = 1;
        }
      }

      durationEventAverage = parseFloat(
        (durationEventTotal / durationUnitConvertNum / durationEventCount).toFixed(2),
      );
      durationEventTotal = parseFloat((durationEventTotal / durationUnitConvertNum).toFixed(2));

      return {
        date,
        attempts: sumAttempts,
        success: sumSuccess,
        instances: sumInstances,
        rate,
        durationEventTotal,
        durationEventAverage,
        type: trialsOfDate[0].data_collection_method ?? '',
        percentage: sumAttempts ? +((100 * sumSuccess) / sumAttempts).toFixed(2) : 0,
      };
    });

    // Sort by date
    const sortedTrials = aggregatedTrials.sort(
      (a, b) => moment(a.date).unix() - moment(b.date).unix(),
    );

    // Calculate trend line if there are data points
    if (sortedTrials.length > 1) {
      const dataPoints = sortedTrials.map((trial) => ({
        x: moment(trial.date).unix(),
        y: trial[getDataKeyByDCM(dcm, durationChartPlotMethod)],
      }));

      const [firstPoint, lastPoint] = [dataPoints[0], dataPoints[dataPoints.length - 1]];
      const { slope, intercept } = linearRegression([firstPoint, lastPoint]);

      return sortedTrials.map((trial) => {
        const x = moment(trial.date).unix();
        const trendKey = `${dcm}_trend`;
        return x === firstPoint.x || x === lastPoint.x
          ? { ...trial, [trendKey]: slope * x + intercept }
          : trial;
      });
    }

    return sortedTrials;
  }, [trialSummary?.trials]);

  const phaseChangeData = useMemo(() => {
    if (!trialSummary?.phase_changes?.length) return [];

    const maxYValue = Math.max(...trialSummary.trials.map((trial) => trial[dataKey] ?? 0)) + 5;

    return trialSummary.phase_changes.flatMap((phase: ITargetPhaseChange) => [
      {
        date: moment(phase.date_added).format('YYYY-MM-DD'),
        [phase.title]: 0,
      },
      {
        date: moment(phase.date_added).format('YYYY-MM-DD'),
        [phase.title]: maxYValue,
      },
    ]);
  }, [trialSummary?.phase_changes, trialSummary?.trials]);

  const combinedChartData = useMemo(
    () =>
      trendLineData.length
        ? [...trendLineData, ...phaseChangeData].sort(
            (a, b) => moment(a.date).unix() - moment(b.date).unix(),
          )
        : [],
    [trendLineData, phaseChangeData],
  );

  const average = useMemo(() => {
    if (!trendLineData.length) return 0;

    const validTrials = trendLineData.filter((trial) => !isNaN(trial[dataKey] ?? 0));
    if (!validTrials.length) return 0;

    const sum = validTrials.reduce((acc, trial) => acc + (trial[dataKey] ?? 0), 0);
    return sum / validTrials.length;
  }, [trendLineData]);

  const totalDuration = useMemo(() => {
    if (!trendLineData.length || durationChartPlotMethod !== DurationChartPlotMethod.TOTAL)
      return 0;

    const validTrials = trendLineData.filter((trial) => !isNaN(trial[dataKey] ?? 0));
    if (!validTrials.length) return 0;

    return validTrials.reduce((acc, trial) => acc + (trial.durationEventTotal ?? 0), 0);
  }, [trendLineData, durationChartPlotMethod]);

  return {
    trendLineData,
    average,
    phaseChangeData,
    combinedChartData,
    dcm,
    rateUnit,
    durationUnit,
    totalDuration,
  };
}

function TargetProgress({ trialSummary, onUpdateField, target, goalId }: TargetProgressProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const { tpGoalsTrialSummary } = useSelector((state: RootState) => state.goals);
  const { loading: sessionsLoading } = useSelector((state: RootState) => state.sessions);
  const [durationChartPlotMethod, setDurationChartPlotMethod] = useState<DurationChartPlotMethod>(
    () => DurationChartPlotMethod.AVERAGE,
  );

  const { average, combinedChartData, dcm, rateUnit, durationUnit, totalDuration } =
    useChartCalculations(trialSummary, durationChartPlotMethod);
  const getAndUpdateTrialSummary = async () => {
    // If we already have an trial summary for this goal ID return
    if (tpGoalsTrialSummary[goalId]) {
      return;
    }

    const params = {
      client_id: treatmentPlan?.client_id,
      start_date: moment().subtract(364, 'days').format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'),
      goal_id: goalId,
    };
    try {
      const res = await dispatch(
        sessionActions.getTrialSummary({ params, getAccessTokenSilently }),
      );

      if (!res.error) {
        dispatch(
          goalActions.updateState({
            key: 'tpGoalsTrialSummary',
            value: {
              ...tpGoalsTrialSummary,
              [goalId]: res.payload,
            },
          }),
        );
      }
    } catch {
      toast.error('An unexpected error occurred while fetching trial data');
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', mt: 2 }}>
      {sessionsLoading && <FProgressBar />}
      <FHAccordion
        sx={{
          boxShadow: 'none',
          border: `1px solid ${FORTA_GRAY2}`,
          borderRadius: '10px',
        }}
        expanded={Boolean(target?.client_progress_expanded)}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMoreRounded sx={{ width: '40px', height: '40px', color: FORTA_BLUE.primary }} />
          }
          sx={{
            py: 2,
            '& .MuiAccordionSummary-content': {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            },
          }}
          onClick={() => {
            onUpdateField('client_progress_expanded', !target?.client_progress_expanded);
            getAndUpdateTrialSummary();
          }}
        >
          <Typography fontWeight={700} fontSize={18}>
            Client&apos;s progress
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {trialSummary && trialSummary?.trials.length > 0 && !sessionsLoading && (
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              gap={1}
              alignItems={'center'}
              mb={2}
            >
              <Box sx={{ display: 'flex' }}>
                {dcm === TPGoalDataCollectionMethod.DURATION ? (
                  <>
                    <Typography fontWeight={700} fontSize={16} textAlign={'left'}>
                      {`${
                        durationChartPlotMethod === DurationChartPlotMethod.TOTAL
                          ? 'Total'
                          : 'Average'
                      } duration (in ${durationUnit.toLowerCase()}):`}
                    </Typography>
                    <Typography fontSize={16} textAlign={'left'} ml={1}>
                      {durationChartPlotMethod === DurationChartPlotMethod.TOTAL
                        ? totalDuration
                        : `${
                            Number.isInteger(average) ? average : ((average * 10) / 10).toFixed(2)
                          }`}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography fontWeight={700} fontSize={16} textAlign={'left'}>
                      Average{' '}
                      {dcm === TPGoalDataCollectionMethod.PERCENTAGE
                        ? 'success percentage'
                        : dcm === TPGoalDataCollectionMethod.INSTANCES
                          ? 'Instances'
                          : `rate (per ${rateUnit.substring(0, rateUnit.length - 1)})`}
                    </Typography>
                    <Typography fontSize={16} textAlign={'left'} ml={1}>
                      {`${Number.isInteger(average) ? average : ((average * 10) / 10).toFixed(2)}${
                        dcm === TPGoalDataCollectionMethod.PERCENTAGE ? '%' : ''
                      }`}
                    </Typography>
                  </>
                )}
              </Box>
              <Box>
                {dcm === TPGoalDataCollectionMethod.DURATION && (
                  <Box sx={{ display: 'flex', alignItems: 'center', width: 360 }}>
                    <Typography fontWeight="bold" align="right" width={'200px'} mr={1}>
                      Display chart by:
                    </Typography>
                    <FHSelect
                      sx={{ width: '180px' }}
                      inputHeight={40}
                      backgroundType={BackgroundTypes.Colored}
                      disabled={combinedChartData?.length === 0}
                      options={DurationChartPlotOptions}
                      optionDataTestIdPrefix="baseLineLevelOption"
                      value={durationChartPlotMethod}
                      onUpdate={(value) => {
                        if (
                          value === DurationChartPlotMethod.AVERAGE ||
                          value === DurationChartPlotMethod.TOTAL
                        ) {
                          setDurationChartPlotMethod(value);
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}
          {sessionsLoading ? (
            <Box display={'flex'} alignItems={'center'} justifyContent={'center'} gap={1} my={2}>
              <Typography fontSize={18}>Loading...</Typography>
            </Box>
          ) : trialSummary && trialSummary?.trials.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%" minHeight={350}>
              <LineChart
                data={combinedChartData}
                margin={{
                  top: 10,
                  right: 40,
                  left: 0,
                  bottom: 20,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  allowDuplicatedCategory={false}
                  allowDataOverflow
                  tickFormatter={(date) => moment(date).format('MM/DD/YY')}
                  label={{
                    value: 'Date',
                    stroke: FORTA_BLUE.primary,
                    fontWeight: 200,
                    dy: 45,
                  }}
                />
                <YAxis
                  dataKey={getDataKeyByDCM(dcm, durationChartPlotMethod)}
                  domain={[0, dcm === TPGoalDataCollectionMethod.PERCENTAGE ? 100 : 'dataMax + 3']}
                  tickFormatter={(value) =>
                    `${Math.round(value)}${
                      dcm === TPGoalDataCollectionMethod.PERCENTAGE ? '%' : ''
                    }`
                  }
                  label={{
                    value: getChartLabelByDCM(dcm, durationChartPlotMethod),
                    angle: -90,
                    position: 'insideLeft',
                    stroke: FORTA_BLUE.primary,
                    fontWeight: 200,
                  }}
                />
                <Tooltip
                  cursor={{ visibility: 'default', stroke: FORTA_GRAY3, strokeWidth: 1 }}
                  content={(props) => (
                    <CustomTooltip {...props} durationChartPlotMethod={durationChartPlotMethod} />
                  )}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey={getDataKeyByDCM(dcm, durationChartPlotMethod)}
                  stroke={chartColors[0]}
                  strokeWidth={1.5}
                  activeDot={{ r: 8 }}
                  connectNulls
                  name={dcm}
                />

                {/* Trend line */}
                <Line
                  connectNulls
                  type="linear"
                  dataKey={`${dcm}_trend`}
                  stroke={FORTA_BLUE.primary}
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                  dot={false}
                  activeDot={false}
                  name="Trend"
                  legendType="line"
                />

                {/* Phase change lines */}
                {trialSummary?.phase_changes?.map((phase: ITargetPhaseChange, index: number) => (
                  <Line
                    key={phase.id}
                    dataKey={phase.title}
                    strokeDasharray="6 2"
                    stroke={chartColors[1]}
                    dot={false}
                    name="Phase change"
                    strokeWidth={1.5}
                    activeDot={{ r: 8 }}
                    legendType={index === 0 ? 'line' : 'none'}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Box display={'flex'} alignItems={'center'} justifyContent={'center'} gap={1} mb={2}>
              <ErrorOutline sx={{ color: FORTA_BLUE }} />
              <Typography fontSize={18}>No trial data available for this target</Typography>
            </Box>
          )}
        </AccordionDetails>
      </FHAccordion>
    </Box>
  );
}

export default TargetProgress;
